import { useRef, useState, type ChangeEvent } from 'react';
import {
  useMyDocuments,
  type CustomerDocumentMeDto,
} from '@/api/useMyDocuments';
import { isMeFetchError } from '@/api/classify';
import {
  Card,
  EmptyState,
  ErrorBanner,
  PageHeader,
  PrimaryButton,
  StageBadge,
} from '@/components/AppShell';
import { formatDateTime } from '@/utils/formatDate';
import { formatByteSize } from '@/utils/fileSize';

/**
 * 资料上传与列表（Issue #198）。
 *
 * 红线：
 *   - 上传走真实 grant → PUT → finalize 链路（在 useMyDocuments 中）。
 *   - 客户端不写假状态；任一阶段失败都通过 ErrorBanner 提示并停止流程。
 *   - 文件名 / MIME / 大小在 UI 中可见，不展示 storageKey/storageBucket 等内部字段。
 *   - 不持久化签名 URL；下载链接点击即用、不缓存。
 */
const DOC_KIND_OPTIONS: { value: CustomerDocumentMeDto['docKind']; label: string }[] = [
  { value: 'health_report', label: '健康报告' },
  { value: 'medical_image', label: '医学影像' },
  { value: 'photo', label: '照片' },
  { value: 'video', label: '视频' },
  { value: 'document', label: '文档' },
  { value: 'identity', label: '身份证明' },
  { value: 'other', label: '其他' },
];

export function DocumentsPage() {
  const { documents, loading, error, refetch, uploadDocument, downloadDocument } =
    useMyDocuments();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [docKind, setDocKind] = useState<CustomerDocumentMeDto['docKind']>('document');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [feedbackTone, setFeedbackTone] = useState<'ok' | 'error'>('ok');

  function pickFile(event: ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0] ?? null;
    setPendingFile(file);
    setFeedback(null);
    if (file && !title) setTitle(file.name);
  }

  async function handleUpload(): Promise<void> {
    if (!pendingFile) {
      setFeedbackTone('error');
      setFeedback('请先选择要上传的文件');
      return;
    }
    if (!title.trim()) {
      setFeedbackTone('error');
      setFeedback('请填写文件标题');
      return;
    }
    setSubmitting(true);
    setFeedback(null);
    try {
      const result = await uploadDocument({
        metadata: {
          docKind,
          title: title.trim(),
          mimeType: pendingFile.type || 'application/octet-stream',
          declaredByteSize: pendingFile.size,
        },
        body: pendingFile,
        uploadedByteSize: pendingFile.size,
      });
      if (isMeFetchError(result)) {
        setFeedbackTone('error');
        setFeedback(result.message);
        return;
      }
      setFeedbackTone('ok');
      setFeedback('上传成功，资料进入审核流程');
      setPendingFile(null);
      setTitle('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      refetch();
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDownload(doc: CustomerDocumentMeDto): Promise<void> {
    const result = await downloadDocument({
      documentId: doc.id,
      expectedMimeType: doc.mimeType,
      filename: doc.title,
    });
    if (isMeFetchError(result)) {
      setFeedbackTone('error');
      setFeedback(result.message);
      return;
    }
    setFeedbackTone('ok');
    setFeedback(null);
  }

  return (
    <div>
      <PageHeader
        title="资料上传"
        description="上传您的健康报告、医学影像或其他证明材料，由专属顾问审核后再公开。"
      />

      {error ? <ErrorBanner message={error.message} onRetry={refetch} /> : null}

      <Card className="mb-4 space-y-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">资料类型</span>
          <select
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={docKind}
            onChange={(event) =>
              setDocKind(event.target.value as CustomerDocumentMeDto['docKind'])
            }
          >
            {DOC_KIND_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">资料标题</span>
          <input
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder="例如：2026 年 4 月血常规报告"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">选择文件</span>
          <input
            ref={fileInputRef}
            type="file"
            className="text-sm"
            onChange={pickFile}
          />
        </label>
        {pendingFile ? (
          <p className="text-xs text-muted-foreground">
            待上传：{pendingFile.name} · {formatByteSize(pendingFile.size)}
          </p>
        ) : null}
        {feedback ? (
          <p
            role={feedbackTone === 'error' ? 'alert' : 'status'}
            className={
              feedbackTone === 'error'
                ? 'text-sm text-destructive'
                : 'text-sm text-emerald-700'
            }
          >
            {feedback}
          </p>
        ) : null}
        <PrimaryButton onClick={() => void handleUpload()} disabled={submitting} full>
          {submitting ? '上传中…' : '提交资料'}
        </PrimaryButton>
      </Card>

      <h2 className="mb-2 text-sm font-semibold">我的资料</h2>
      {loading ? (
        <Card>
          <p className="text-sm text-muted-foreground">加载资料列表…</p>
        </Card>
      ) : documents.length === 0 ? (
        <Card>
          <EmptyState
            title="还没有上传过资料"
            description="请使用上方表单上传第一份资料。"
          />
        </Card>
      ) : (
        <ul className="space-y-3">
          {documents.map((doc) => (
            <li key={doc.id}>
              <DocumentRow doc={doc} onDownload={() => void handleDownload(doc)} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface DocumentRowProps {
  doc: CustomerDocumentMeDto;
  onDownload: () => void;
}

function DocumentRow({ doc, onDownload }: DocumentRowProps) {
  return (
    <Card className="space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{doc.title}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {doc.mimeType} · {formatByteSize(doc.declaredByteSize)}
          </p>
        </div>
        <ReviewStateBadge state={doc.reviewState} />
      </div>
      <p className="text-xs text-muted-foreground">
        上传时间 {formatDateTime(doc.createdAt)}
      </p>
      {doc.rejectReason && doc.reviewState === 'rejected' ? (
        <p className="rounded-md bg-destructive/5 px-2 py-1 text-xs text-destructive">
          审核未通过：{doc.rejectReason}
        </p>
      ) : null}
      {doc.customerVisible && doc.reviewState === 'published' ? (
        <button
          type="button"
          onClick={onDownload}
          className="text-xs font-medium text-primary underline-offset-2 hover:underline"
        >
          下载已审核版本
        </button>
      ) : null}
    </Card>
  );
}

function ReviewStateBadge({ state }: { state: CustomerDocumentMeDto['reviewState'] }) {
  switch (state) {
    case 'awaiting_upload':
      return <StageBadge label="待上传" tone="neutral" />;
    case 'pending_review':
      return <StageBadge label="审核中" tone="progress" />;
    case 'approved':
      return <StageBadge label="已通过审核" tone="good" />;
    case 'rejected':
      return <StageBadge label="审核未通过" tone="warn" />;
    case 'published':
      return <StageBadge label="已发布" tone="good" />;
    case 'retracted':
      return <StageBadge label="已撤回" tone="warn" />;
    default:
      return <StageBadge label={state} tone="neutral" />;
  }
}
